import { withSSR } from 'withSSR';
import { Dispatch } from 'redux';
import { NextPage } from 'next';
import { useSelector } from 'react-redux';
import Head from 'next/head';

import { ReduxState, wrapper } from 'ducks';
import { fetchCustomTopPageServerSide, fetchSettingsServerSide } from 'ducks/server';
import { detectLocale } from 'i18n-server';
import { selectLanguageOptions } from 'ducks/server/settings';
import { getI18nServerSideProps } from 'i18n';
import { absoluteUrl } from 'lib/util/absoluetUrl';
import { fetchConfigForHostname } from 'lib/util/fetchConfigForHostname';
import { SignIn } from 'components/Customer/SignIn';
import { isSigiraLogin } from 'lib/util/isSigiraLogin';
import { SigiraSignIn } from 'components/Customer/SigiraSignIn';
import { BookingWidgetLayout } from 'components/BookingWidgetLayout/BookingWidgetLayout';

interface Props {
  apiKey: string;
}

const Page: NextPage<Props> = () => {
  const supplierId = useSelector((state: ReduxState) => state.server.settings.all.supplier_id);
  return (
    <>
      <Head>
        <meta name="robots" content="noindex" />
      </Head>
      <BookingWidgetLayout>
        {isSigiraLogin(supplierId) ? <SigiraSignIn /> : <SignIn />}
      </BookingWidgetLayout>
    </>
  );
};

export const getServerSideProps = wrapper.getServerSideProps(async ({ req, res, store }) => {
  const hostname = req.headers?.host?.split(':')[0] || '';
  const { apiKey, lang } = await fetchConfigForHostname(hostname);

  const dispatch: Dispatch<any> = store.dispatch;

  let i18nProps = {};

  let locale = lang;
  if (locale) {
    // If lang is initialized in config, this is a single-language PMP

    await Promise.all([
      dispatch(fetchSettingsServerSide(apiKey, '', locale, `https://${hostname}`)),
    ]);
  } else {
    locale = await detectLocale(req, res);

    // Fetch settings using detected language
    await dispatch(fetchSettingsServerSide(apiKey, '', locale));

    // Verify that the detected language is in the supplier's supported languages. If it isn't,
    // default to the first supported language and refetch settings.
    const langOptions = selectLanguageOptions(store.getState());
    if (langOptions.length > 0 && !langOptions.some((langOpt) => langOpt.iso === locale)) {
      locale = langOptions[0].iso;
      await dispatch(fetchSettingsServerSide(apiKey, '', locale));
    }
  }

  const isCustomTopPageEnabled = store.getState().server.settings.all?.is_custom_top_page_enabled;

  if (isCustomTopPageEnabled) {
    await dispatch(fetchCustomTopPageServerSide(apiKey, locale));
  }

  i18nProps = await getI18nServerSideProps(locale, true);
  return {
    props: {
      apiKey,
      host: absoluteUrl(req),
      ...i18nProps,
    },
  };
});

export default withSSR()(Page);
